"use client";

import { Link, useNavigate } from "@tanstack/react-router";
import {
  Coins,
  KeyRound,
  LogOutIcon,
  Moon,
  ShieldCheck,
  Sun,
  UserIcon,
} from "lucide-react";
import { usePostHog } from "@posthog/react/slim";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@super-resume-new/ui/components/avatar";
import {
  Menu,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuTrigger,
} from "@super-resume-new/ui/components/menu";
import { Skeleton } from "@super-resume-new/ui/components/skeleton";

import { authClient } from "@/lib/auth-client";
import { useAuthModal } from "@/context/auth-modal";
import { getInitials } from "@/utils/format";
import { ExperimentalBadge } from "@/components/experimental-badge";
import { ThemeToggle } from "./theme-toggle";

export default function UserMenu() {
  const navigate = useNavigate();
  const { data: session, isPending } = authClient.useSession();
  const { openAuthModal } = useAuthModal();
  const posthog = usePostHog();
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  if (!mounted || isPending) {
    return <Skeleton className="size-8 rounded-full" />;
  }

  if (!session) {
    return (
      <div className="flex items-center gap-2">
        <ThemeToggle />
        <button
          type="button"
          onClick={() => openAuthModal({ defaultTab: "signin" })}
          className="rounded-md border border-border bg-transparent px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-accent cursor-pointer"
        >
          Sign In
        </button>
      </div>
    );
  }

  function handleSignOut() {
    authClient.signOut({
      fetchOptions: {
        onSuccess: () => {
          posthog.reset();
          navigate({ to: "/" });
        },
      },
    });
  }

  const initials = getInitials(session.user.name || session.user.email);

  return (
    <Menu>
      <MenuTrigger
        render={
          <button
            type="button"
            aria-label="User menu"
            className="rounded-full outline-none ring-offset-background transition focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
          />
        }
      >
        <Avatar className="size-8 cursor-pointer ring-2 ring-border hover:ring-brand transition">
          <AvatarImage src={session.user.image ?? undefined} alt={session.user.name} />
          <AvatarFallback className="text-xs font-semibold">{initials}</AvatarFallback>
        </Avatar>
      </MenuTrigger>

      <MenuPopup align="end" sideOffset={8} className="w-60">
        {/* User info header */}
        <div className="px-2.5 py-2">
          <p className="text-sm font-semibold text-foreground leading-none">{session.user.name}</p>
          <p className="mt-1 text-xs text-muted-foreground truncate">{session.user.email}</p>
        </div>

        <MenuSeparator />

        <MenuItem render={<Link to="/account" />}>
          <UserIcon className="size-4" />
          Account &amp; Profile
        </MenuItem>

        {(session.user as any)?.role === "admin" && (
          <MenuItem
            render={<Link to="/admin" className="flex items-center justify-between w-full" />}
          >
            <span className="flex items-center gap-2 font-medium text-brand-foreground">
              <ShieldCheck className="size-4" />
              Admin Console
            </span>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-brand-foreground bg-brand/10 px-1.5 py-0.5 rounded border border-brand/20">
              Admin
            </span>
          </MenuItem>
        )}

        <MenuItem render={<Link to="/credits" />}>
          <Coins className="size-4" />
          Credits &amp; Usage
        </MenuItem>

        <MenuItem
          render={<Link to="/api-keys" className="flex items-center justify-between w-full" />}
        >
          <span className="flex items-center gap-2">
            <KeyRound className="size-4" />
            API Keys &amp; MCP
          </span>
          <ExperimentalBadge text="Beta" />
        </MenuItem>

        <MenuItem onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}>
          {resolvedTheme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
          <span>{resolvedTheme === "dark" ? "Light Mode" : "Dark Mode"}</span>
        </MenuItem>

        <MenuSeparator />

        <MenuItem variant="destructive" onClick={handleSignOut}>
          <LogOutIcon className="size-4" />
          Sign Out
        </MenuItem>
      </MenuPopup>
    </Menu>
  );
}
